import { usePercent } from '@afterpaytouch/core'
import { Country, ErrorCode } from '@afterpaytouch/portal-api'
import { useTranslation } from 'next-i18next'
import { PaymentInterval } from '../../model'
import {
  useAppRouter,
  usePaymentInterval,
  useOfferDetailPCL,
  useOfferDetailPBI,
  useCountry,
  useRatesConfig,
  useHasZeroAprConsumerLendingPromotion,
  useMerchantName,
  useIsAfterpayFinanceFeeEligible,
  useSUPFinanceFee,
} from '../../state'
import { Currency } from '@afterpay/types'
import { useMoney } from '../Money/useMoney'

const useFormatMoney = (currency?: Currency, decimalPlaces: number = 2): any => {
  const { locale } = useAppRouter()
  const { formatMoney } = useMoney({
    locale,
    currency,
    decimalPlaces,
  })
  return formatMoney
}

export const getConsumerInitials = (name: string): string => {
  const getFirstLetterOfWord = (word: string | undefined): string => {
    return typeof word !== 'undefined' ? word[0] : ''
  }

  const words: string[] = name.split(' ')
  const firstWord = words.shift()
  const lastWord = words.pop()
  return [getFirstLetterOfWord(firstWord), getFirstLetterOfWord(lastWord)].join('').toUpperCase()
}

export const usePCLFromText = (): string => {
  const { t } = useTranslation()
  const { monthlyAmount } = useOfferDetailPCL() ?? {}

  const formatMoneyForMonthlyAmount = useFormatMoney(monthlyAmount?.currency)

  // i.e. From $210.00/month
  return t('consumerLending:paymentOptions:payInMoreAmount', {
    monthlyAmount: formatMoneyForMonthlyAmount(monthlyAmount?.amount),
  })
}

export const usePCLNumberOfInstalmentsText = (): string => {
  const { t } = useTranslation()
  const { numberOfInstallments, amountDueToday } = useOfferDetailPCL() ?? {}

  const formatMoney = useFormatMoney(amountDueToday?.currency, 0)

  // i.e. Up to 24 months with $0 due today
  return t('consumerLending:paymentOptions:upToNumberOfInstallmentsMonths', {
    numberOfInstallments,
    amountDueToday: formatMoney(amountDueToday?.amount),
  })
}

export const usePCLAsLowAsText = (): string => {
  const { locale } = useAppRouter()
  const { formatPercent } = usePercent({ locale, decimalPlaces: 2, shouldDivideBy100: true })
  const { apr } = useOfferDetailPCL() ?? {}
  const { t } = useTranslation()
  const country = useCountry()
  const ratesConfig = useRatesConfig()
  const hasZeroAprPromotion = useHasZeroAprConsumerLendingPromotion()
  const merchant = useMerchantName()

  if (country === Country.AU) {
    // i.e. From 4% p.a. (comparison rate 4.23% p.a.)
    return t('consumerLending:paymentOptions:payInMoreAPR', {
      lowestPercentage: formatPercent(apr),
      comparisonRate: ratesConfig.minComparisonRate,
    })
  }
  // i.e. As low as 30.00% APR
  return hasZeroAprPromotion
    ? t('consumerLending:paymentOptions:payInMoreWithZeroAPR', { merchant })
    : t('consumerLending:paymentOptions:payInMoreAPR', { lowestPercentage: formatPercent(apr) })
}

export const usePBIDueTodayText = (): string => {
  const { t } = useTranslation()
  const { amountDueToday } = useOfferDetailPBI() ?? {}

  const formatMoney = useFormatMoney(amountDueToday?.currency)

  // i.e. $55.00 dueToday
  return t('consumerLending:paymentOptions:dueToday', {
    amountDueToday: formatMoney(amountDueToday?.amount),
  })
}

export const usePBIEveryText = (): string => {
  const { t } = useTranslation()
  const paymentInterval = usePaymentInterval()
  const { monthlyAmount } = useOfferDetailPBI() ?? {}
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const formatMoney = useFormatMoney(monthlyAmount?.currency)
  const translationParams = { instalmentAmount: formatMoney(monthlyAmount?.amount), context: isAFF ? 'aff' : undefined }
  // i.e. $55.00 every 2 weeks / every month
  return paymentInterval === PaymentInterval.FORTNIGHTLY
    ? t('consumerLending:paymentOptions:fortnightlyPaymentInterval', translationParams)
    : t('consumerLending:paymentOptions:monthlyPaymentInterval', translationParams)
}

export const usePBIAPRText = (): string => {
  const { t } = useTranslation()
  const { locale } = useAppRouter()
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const supFinanceFee = useSUPFinanceFee()
  const { formatPercent } = usePercent({ locale, decimalPlaces: 0, shouldDivideBy100: true })
  const interestAmount = '0.00'
  const apr = formatPercent(0)

  const { monthlyAmount } = useOfferDetailPBI() ?? {}

  const formatMoney = useFormatMoney(monthlyAmount?.currency)

  if (isAFF) {
    // Incl. $12.42 finance fee
    return t('consumerLending:paymentOptions:payInFourAFF', {
      financeFee: formatMoney(supFinanceFee?.variableAmount.amount),
    })
  }

  // Interest $0.00 (0% APR)
  return t('consumerLending:paymentOptions:payInFourAPR', {
    apr,
    interestAmount: formatMoney(interestAmount),
  })
}

const rejectedReasonCodeKey = (rejectedReasonCode: ErrorCode | undefined): string => {
  switch (rejectedReasonCode) {
    case ErrorCode.MaxActiveOrdersReached:
      return 'maxActiveOrdersReached'
    case ErrorCode.AmountTooHigh:
      return 'amountTooHigh'
    case ErrorCode.OrderAmountTooHigh:
      return 'amountTooHigh'
    case ErrorCode.MerchantOrderAmountLimitExceeded:
      return 'merchantOrderAmountLimitExceeded'
    default:
      return 'genericDecline'
  }
}

export const usePBIRejectedText = (): string => {
  const { t } = useTranslation()
  const offerDetailPBI = useOfferDetailPBI()

  return t(`consumerLending:paymentTypesPBIDeclined:${rejectedReasonCodeKey(offerDetailPBI?.rejectedReasonCode)}`, {
    interval: offerDetailPBI?.numberOfInstallments,
  })
}
